export const MIN_QUERY_LENGTH = 2;
export const MAX_HITS = 200;
const CONTEXT = 40;
const fold = (char) => char.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
/** En minuscules et sans accents, pour que « ile » trouve « île » ; chaque caractère garde sa place dans le texte d'origine. */
export function indexText(text, dirty = []) {
    let lookup = "";
    const map = [];
    let i = 0;
    for (const char of text) {
        for (const piece of fold(char)) {
            lookup += piece;
            map.push(i);
        }
        i += char.length;
    }
    return { text, dirty, lookup, map };
}
export const normalizeQuery = (query) => indexText(query.replace(/\s+/g, " ").trim()).lookup;
const LIGATURES = { "\ufb00": "ff", "\ufb01": "fi", "\ufb02": "fl", "\ufb03": "ffi", "\ufb04": "ffl", "\ufb05": "st", "\ufb06": "st" };
// Zones à usage privé (glyphes d'une police, sans valeur de texte), caractères de contrôle et marques invisibles.
const NOT_TEXT = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u2028-\u202e\u2060-\u2064\ue000-\uf8ff\ufeff\ufff9-\uffff]/;
/** Le texte tel qu'il se lit : ligatures écrites en toutes lettres, glyphes sans valeur retirés, endroits modifiés notés. */
export function cleanText(raw) {
    let text = "";
    const dirty = [];
    for (const char of raw) {
        const written = LIGATURES[char];
        if (written) {
            dirty.push(text.length);
            text += written;
        }
        else if (NOT_TEXT.test(char) || /[\u{f0000}-\u{10ffff}]/u.test(char)) {
            dirty.push(text.length);
        }
        else {
            text += char;
        }
    }
    return { text, dirty };
}
/** Le texte lisible d'un fichier XHTML, dans l'ordre de lecture. */
export function plainText(markup, parser = new DOMParser()) {
    const parse = (type) => parser.parseFromString(markup, type);
    let doc = parse("application/xhtml+xml");
    if (doc.querySelector("parsererror"))
        doc = parse("text/html");
    doc.querySelectorAll("script, style, head, nav[epub\\:type='toc']").forEach((node) => node.remove());
    // Un bloc se termine par une espace : le dernier mot d'un paragraphe et le premier du suivant restent séparés.
    doc.querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, br, tr, blockquote").forEach((node) => node.append(" "));
    return cleanText((doc.body?.textContent ?? doc.documentElement?.textContent ?? "").replace(/\s+/g, " ").trim());
}
/** Les occurrences de `needle` (déjà normalisée) dans `file`, en extraits du texte d'origine. */
export function findInText(file, needle, limit = Infinity) {
    const hits = [];
    if (!needle)
        return hits;
    const changedBetween = (from, to) => file.dirty.some((place) => place >= from && place <= to);
    let from = 0;
    while (hits.length < limit) {
        const found = file.lookup.indexOf(needle, from);
        if (found < 0)
            break;
        const start = file.map[found];
        const lastChar = file.map[found + needle.length - 1];
        const end = lastChar + String.fromCodePoint(file.text.codePointAt(lastChar)).length;
        const before = file.text.slice(Math.max(0, start - CONTEXT), start);
        const after = file.text.slice(end, end + CONTEXT);
        hits.push({
            before,
            match: file.text.slice(start, end),
            after,
            anchorBefore: changedBetween(start - CONTEXT, start) ? "" : before,
            anchorAfter: changedBetween(end, end + CONTEXT) ? "" : after,
            ratio: start / Math.max(1, file.text.length),
        });
        from = found + needle.length;
    }
    return hits;
}
/** Recherche dans le texte de chaque fichier du livre (les fichiers sont lus une fois et gardés). */
export function createBookSearch(publication, options = {}) {
    const files = publication.readingOrder.items;
    const cache = new Map();
    const load = async (index) => {
        const link = files[index];
        const known = cache.get(link.href);
        if (known)
            return known;
        let cleaned = { text: "", dirty: [] };
        try {
            cleaned = plainText((await publication.get(link).readAsString()) ?? "");
        }
        catch {
            // Un fichier illisible n'a pas d'occurrence.
        }
        const indexed = indexText(cleaned.text, cleaned.dirty);
        cache.set(link.href, indexed);
        return indexed;
    };
    return {
        async run(query, { signal, onHits, onProgress }) {
            const needle = normalizeQuery(query);
            let total = 0;
            let truncated = false;
            if (needle.length < MIN_QUERY_LENGTH)
                return { truncated };
            for (let i = 0; i < files.length; i++) {
                if (signal.aborted)
                    break;
                const link = files[i];
                const file = await load(i);
                if (signal.aborted)
                    break;
                const found = findInText(file, needle, MAX_HITS - total);
                const type = link.type ?? "application/xhtml+xml";
                const label = options.labelOf?.(link.href, i);
                onHits(found.map((hit) => {
                    const position = options.positionOf?.(link.href, hit.ratio);
                    return {
                        before: hit.before,
                        match: hit.match,
                        after: hit.after,
                        section: label,
                        // Le texte exact avec son contexte : Readium retrouve cette occurrence-là dans la page.
                        locator: {
                            href: link.href,
                            type,
                            ...(label ? { title: label } : {}),
                            locations: {
                                progression: hit.ratio,
                                ...(position !== undefined ? { position } : {}),
                                totalProgression: (i + hit.ratio) / files.length,
                            },
                            text: { before: hit.anchorBefore, highlight: hit.match, after: hit.anchorAfter },
                        },
                    };
                }));
                total += found.length;
                onProgress(i + 1, files.length);
                if (total >= MAX_HITS) {
                    truncated = i < files.length - 1 || found.length >= MAX_HITS;
                    break;
                }
            }
            return { truncated };
        },
    };
}
//# sourceMappingURL=search.js.map